import React from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import {
  LuAlertCircle,
  LuBoxes,
  LuFileText,
  LuPlus,
  LuTrash2,
} from "react-icons/lu";

import ItemPhoto from "../itemPhoto";
import { TagIcon } from "../icons";
import Input from "../ui/Input";
import Textarea from "../ui/Textarea";
import cx from "../ui/cx";
import {
  arrayAddButtonClass,
  arrayRemoveButtonClass,
  arrayRowClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  fieldTextareaClass,
  formFieldIconClass,
} from "../ui/styles";
import { inheritedSizeRows } from "../../static/itemSize";
import { IFormInput, ISubItemFormInput } from "../../types/item";
import ItemMaterialField from "./ItemMaterialField";
import ItemSizeRates from "./ItemSizeRates";

export const emptySubItem: ISubItemFormInput = {
  sub_item_id: null,
  saved_code: null,
  code: null,
  description: "",
  image: null,
  image_url: null,
  pieces: "1",
  material_id: null,
  cutting_rate: "",
  turning_rate: "",
  vmc_rate: "",
  cut_length: "",
  sizes: [],
};

// The number after "{prefix}-" in a code, or 0. Case-insensitive like the server's generateCode().
const numberAfter = (code: string, prefix: string) => {
  const start = `${prefix}-`.toLowerCase();
  const rest = code.toLowerCase().startsWith(start)
    ? code.slice(start.length)
    : "";
  return /^\d+$/.test(rest) ? Number(rest) : 0;
};

// The code each row is saved with, worked out the way the server does it:
// - a typed code stays as it is;
// - a saved code that starts with the item's saved code keeps its part after it under the
//   item's current code, and any other saved code stays as it is;
// - new rows take the next numbers after the saved rows' codes, passing over typed codes,
//   so typing AB-03-05 on one row doesn't renumber the others.
export const subItemCodes = (
  name: string,
  savedName: string,
  rows: Pick<ISubItemFormInput, "saved_code" | "code">[]
) => {
  const prefix = name.trim();
  const typed = rows.map((row) => row.code?.trim() || "");

  const known = rows.map((row, index) => {
    if (typed[index]) return typed[index];
    if (!row.saved_code) return null;
    if (!row.saved_code.startsWith(`${savedName}-`)) return row.saved_code;
    return prefix ? `${prefix}${row.saved_code.slice(savedName.length)}` : "";
  });

  const typedKeys = new Set(
    typed.filter(Boolean).map((code) => code.toLowerCase())
  );

  let next = Math.max(
    0,
    ...rows.map((row, index) =>
      row.saved_code ? numberAfter(known[index] || "", prefix) : 0
    )
  );

  return known.map((code) => {
    if (code !== null) return code;
    if (!prefix) return "";

    let candidate = "";
    do {
      next += 1;
      candidate = `${prefix}-${String(next).padStart(2, "0")}`;
    } while (typedKeys.has(candidate.toLowerCase()));

    return candidate;
  });
};

// Why each row's code can't be saved, or null. Codes are unique among all items, so a sub
// item can't repeat its item's code or another row's; the server also checks other items.
export const subItemCodeProblems = (name: string, codes: string[]) => {
  const parent = name.trim().toLowerCase();
  const keys = codes.map((code) => code.toLowerCase());

  return keys.map((key, index) => {
    if (!key) return null;
    if (key === parent) return "આ કોડ મુખ્ય આઈટમ નો છે, બીજો કોડ દાખલ કરો";
    if (keys.some((other, otherIndex) => otherIndex !== index && other === key))
      return "આ કોડ બીજી સબ આઈટમ માં પણ છે";
    return null;
  });
};

const rowIndexClass =
  "md:col-span-12 flex items-center justify-between gap-2 text-sm font-semibold text-[#113F67]";

type Props = {
  control: Control<IFormInput>;
  register: UseFormRegister<IFormInput>;
  getValues: UseFormGetValues<IFormInput>;
  setValue: UseFormSetValue<IFormInput>;
  errors: FieldErrors<IFormInput>;
  name: string;
  savedName: string;
};

const SubItemsForm = ({
  control,
  register,
  getValues,
  setValue,
  errors,
  name,
  savedName,
}: Props) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "sub_items",
  });

  // Live values: a row's code depends on what is typed on the other rows.
  const rows = useWatch({ control, name: "sub_items" }) || [];

  const codes = subItemCodes(name, savedName, rows);
  const codeProblems = subItemCodeProblems(name, codes);

  return (
    <div className="space-y-4">
      <label className={fieldLabelClass}>
        સબ આઈટમ્સ <span className="text-red-500">*</span>
      </label>

      {fields.map((field, index) => {
        const rowErrors = errors?.sub_items?.[index];
        const codeProblem = codeProblems[index];

        // An item built from sub items needs at least one, so the last row can't be removed.
        const removeButton = fields.length > 1 && (
          <button
            type="button"
            title="કાઢી નાખો"
            className={arrayRemoveButtonClass}
            onClick={() => remove(index)}
          >
            <LuTrash2 className="h-5 w-5" />
          </button>
        );

        return (
          <div key={field.id} className={arrayRowClass}>
            <div className={rowIndexClass}>
              <span>સબ આઈટમ - {index + 1}</span>
              {removeButton}
            </div>

            <div className="md:col-span-4 space-y-2">
              <label className={fieldLabelClass}>નામ (કોડ)</label>
              <div className={cx("relative", codeProblem && "error")}>
                <TagIcon className={formFieldIconClass} />
                <Controller
                  control={control}
                  name={`sub_items.${index}.code`}
                  render={({ field: codeField }) => (
                    <Input
                      ref={codeField.ref}
                      name={codeField.name}
                      type="text"
                      maxLength={255}
                      value={codeField.value ?? codes[index] ?? ""}
                      placeholder={
                        codes[index] || "આઈટમનું નામ દાખલ કરતાં કોડ બનશે"
                      }
                      className={cx(
                        fieldInputClass(!!codeProblem),
                        "bg-white"
                      )}
                      onChange={(e) => codeField.onChange(e.target.value)}
                      onBlur={() => {
                        // A cleared code goes back to the automatic one.
                        if (codeField.value !== null && !codeField.value.trim()) {
                          codeField.onChange(null);
                        }
                        codeField.onBlur();
                      }}
                    />
                  )}
                />
                {codeProblem && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {codeProblem ? (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {codeProblem}
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  મુખ્ય આઈટમ ના કોડ પરથી આપમેળે બને છે, જરૂર હોય તો બદલી શકાય
                </p>
              )}
            </div>

            <div className="md:col-span-4 space-y-2">
              <label className={fieldLabelClass}>
                મુખ્ય આઈટમ બનાવવા માટે જરૂરી નંગ{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", rowErrors?.pieces && "error")}>
                <LuBoxes className={formFieldIconClass} />
                <Controller
                  control={control}
                  name={`sub_items.${index}.pieces`}
                  rules={{ validate: (value) => Number(value) >= 1 }}
                  render={({ field: piecesField }) => (
                    <Input
                      {...piecesField}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="દા.ત. 1"
                      className={cx(
                        fieldInputClass(!!rowErrors?.pieces),
                        "bg-white"
                      )}
                      onChange={(e) =>
                        piecesField.onChange(e.target.value.replace(/\D/g, ""))
                      }
                    />
                  )}
                />
                {rowErrors?.pieces && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {rowErrors?.pieces && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  ઓછામાં ઓછો 1 નંગ દાખલ કરો
                </p>
              )}
            </div>

            <div className="md:col-span-4">
              <ItemMaterialField
                control={control}
                name={`sub_items.${index}.material_id`}
                error={rowErrors?.material_id}
                idPrefix={`sub-item-${index}`}
              />
            </div>

            {/* The main item's sizes, with this sub item's own cut lengths and rates. From the second row on, the
                cut lengths can be copied from the row above. */}
            <ItemSizeRates
              control={control}
              getValues={getValues}
              setValue={setValue}
              prefix={`sub_items.${index}.`}
              idPrefix={`sub-item-${index}`}
              inheritSizes
              copyFrom={index > 0 ? `sub_items.${index - 1}.` : undefined}
              copyFromNumber={index}
              className="md:col-span-12"
            />

            {/* The description grows to the height of the photo beside it. */}
            <div className="md:col-span-6 flex flex-col space-y-2">
              <label className={fieldLabelClass}>ડિસ્ક્રિપ્શન (વૈકલ્પિક)</label>
              <div className="relative flex flex-1 flex-col">
                <LuFileText className={formFieldIconClass} />
                <Textarea
                  placeholder="ડિસ્ક્રિપ્શન દાખલ કરો"
                  className={cx(fieldTextareaClass(), "flex-1 bg-white")}
                  {...register(`sub_items.${index}.description`, {
                    required: false,
                  })}
                />
              </div>
            </div>

            {/* Laid out like the description, so the two boxes line up. */}
            <div className="md:col-span-6 flex flex-col space-y-2">
              <label className={fieldLabelClass}>ફોટો (વૈકલ્પિક)</label>
              <Controller
                control={control}
                name={`sub_items.${index}.image`}
                render={({ field: photoField }) => (
                  <ItemPhoto
                    value={photoField.value}
                    existingUrl={field.image_url}
                    onChange={photoField.onChange}
                  />
                )}
              />
            </div>
          </div>
        );
      })}

      {/* A new sub item comes with the main item's sizes. */}
      <button
        type="button"
        className={arrayAddButtonClass}
        onClick={() =>
          append({
            ...emptySubItem,
            sizes: inheritedSizeRows(getValues("sizes"), []),
          })
        }
      >
        <LuPlus className="h-4 w-4" />
        સબ આઈટમ ઉમેરો
      </button>
    </div>
  );
};
export default SubItemsForm;
